import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  effect,
  inject,
  input,
  OnInit,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonToggleGroup } from '@angular/material/button-toggle';
import { MatDialog } from '@angular/material/dialog';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import moment from 'moment';
import { catchError, EMPTY, finalize, map, Observable, Subject, switchMap, throwError } from 'rxjs';
import { DIALOG_BUTTONS } from 'src/app/shared/constants';
import { DialogComponent } from 'src/app/shared/dialogs/dialog/dialog.component';
import { MaterialModule } from 'src/app/shared/material.module';
import { EditorComponent } from 'src/app/shared/components/editor/editor.component';
import { MarkdownEditorComponent } from 'src/app/shared/components/markdown-editor/markdown-editor.component';
import { SpinnerOverlayComponent } from 'src/app/shared/components/spinner-overlay/spinner-overlay.component';
import { ContentFormat } from 'src/app/shared/models/page/page.model';
import { AlertService } from 'src/app/shared/services/alert.service';
import { TimeLoggingNotificationService } from 'src/app/shared/services/time-logging-notification.service';
import { NotificationEventResult } from '../enums/notification-event-result.enum';
import { EmailPreview } from '../models/email-preview';
import { NotificationHistoryEntry } from '../models/notification-history-entry';
import { EmailPreviewDialogComponent } from './email-preview-dialog/email-preview-dialog.component';
import {
  NotificationSectionComponent,
  NotificationSectionDate,
} from './notification-section/notification-section.component';

interface NotificationSectionConfig {
  titleKey: string;
  descriptionKey: string;
  recipientsKey: string;
  dates: NotificationSectionDate[];
  result: () => NotificationEventResult | null;
  preview: () => void;
}

@Component({
  selector: 'app-notification-setup',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslateModule,
    ReactiveFormsModule,
    MaterialModule,
    SpinnerOverlayComponent,
    NotificationSectionComponent,
    EditorComponent,
    MarkdownEditorComponent,
  ],
  templateUrl: './notification-setup.component.html',
  styleUrl: './notification-setup.component.scss',
})
export class NotificationSetupComponent implements OnInit {
  private notificationService = inject(TimeLoggingNotificationService);
  private alertService = inject(AlertService);
  private dialog = inject(MatDialog);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  history = input<NotificationHistoryEntry[]>([]);
  month = input.required<moment.Moment>();

  readonly NotificationEventResult = NotificationEventResult;

  deadlineDay = signal<number>(25);
  hasChanges = signal(false);
  saved = output<void>();
  employeeReminderResult = signal<NotificationEventResult | null>(null);
  opsReminderResult = signal<NotificationEventResult | null>(null);
  workReportLeadsResult = signal<NotificationEventResult | null>(null);
  timeLogNotificationResult = signal<NotificationEventResult | null>(null);
  benefitsResult = signal<NotificationEventResult | null>(null);
  payhawkResult = signal<NotificationEventResult | null>(null);
  teamsRatesResult = signal<NotificationEventResult | null>(null);
  monthlyInvoicesResult = signal<NotificationEventResult | null>(null);
  loadingPreview = signal(false);
  isConvertingBody = signal(false);

  readonly formatToggleGroup = viewChild('formatToggle', { read: MatButtonToggleGroup });

  private bodyFormatChange$ = new Subject<ContentFormat>();

  readonly sections: NotificationSectionConfig[] = [
    {
      titleKey: 'ADMIN.DAYS_LEFT.EMPLOYEE_REMINDER_TITLE',
      descriptionKey: 'ADMIN.DAYS_LEFT.EMPLOYEE_REMINDER_DESCRIPTION',
      recipientsKey: 'ADMIN.DAYS_LEFT.ALL_ACTIVE_EMPLOYEES',
      dates: [
        {
          controlName: 'notification',
          labelKey: 'ADMIN.DAYS_LEFT.NOTIFICATION_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.NOTIFICATION_CAPTION',
          showAfterDeadlineError: true,
        },
        {
          controlName: 'deadline',
          labelKey: 'ADMIN.DAYS_LEFT.DEADLINE_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.DEADLINE_CAPTION',
        },
      ],
      result: this.employeeReminderResult,
      preview: () => this.onPreviewEmail(),
    },
    {
      titleKey: 'ADMIN.DAYS_LEFT.OPS_REMINDER_TITLE',
      descriptionKey: 'ADMIN.DAYS_LEFT.OPS_REMINDER_DESCRIPTION',
      recipientsKey: 'ADMIN.DAYS_LEFT.OPERATIONS',
      dates: [
        {
          controlName: 'opsReminder',
          labelKey: 'ADMIN.DAYS_LEFT.SEND_ON_DAY_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.OPS_REMINDER_CAPTION',
        },
      ],
      result: this.opsReminderResult,
      preview: () => this.onPreviewOpsEmail(),
    },
    {
      titleKey: 'ADMIN.DAYS_LEFT.WORK_REPORT_LEADS_TITLE',
      descriptionKey: 'ADMIN.DAYS_LEFT.WORK_REPORT_LEADS_DESCRIPTION',
      recipientsKey: 'ADMIN.DAYS_LEFT.ALL_LEADS',
      dates: [
        {
          controlName: 'workReportLeads',
          labelKey: 'ADMIN.DAYS_LEFT.WORK_REPORT_LEADS_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.WORK_REPORT_LEADS_CAPTION',
        },
        {
          controlName: 'workReportDeadline',
          labelKey: 'ADMIN.DAYS_LEFT.WORK_REPORT_DEADLINE_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.WORK_REPORT_DEADLINE_CAPTION',
        },
      ],
      result: this.workReportLeadsResult,
      preview: () => this.onPreviewWorkReportLeadsEmail(),
    },
    {
      titleKey: 'ADMIN.DAYS_LEFT.TIME_LOGGING_NOTIFICATION_TITLE',
      descriptionKey: 'ADMIN.DAYS_LEFT.TIME_LOGGING_NOTIFICATION_DESCRIPTION',
      recipientsKey: 'ADMIN.DAYS_LEFT.OPERATIONS',
      dates: [
        {
          controlName: 'missingTimeLogFirst',
          labelKey: 'ADMIN.DAYS_LEFT.TIME_LOGGING_FIRST_NOTIFICATION_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.TIME_LOGGING_FIRST_NOTIFICATION_CAPTION',
        },
        {
          controlName: 'missingTimeLogSecond',
          labelKey: 'ADMIN.DAYS_LEFT.TIME_LOGGING_SECOND_NOTIFICATION_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.TIME_LOGGING_SECOND_NOTIFICATION_CAPTION',
        },
      ],
      result: this.timeLogNotificationResult,
      preview: () => this.onPreviewMissingTimeLogEmail(),
    },
    {
      titleKey: 'ADMIN.DAYS_LEFT.BENEFITS_REMINDER_TITLE',
      descriptionKey: 'ADMIN.DAYS_LEFT.BENEFITS_REMINDER_DESCRIPTION',
      recipientsKey: 'ADMIN.DAYS_LEFT.TC',
      dates: [
        {
          controlName: 'benefits',
          labelKey: 'ADMIN.DAYS_LEFT.BENEFITS_NOTIFICATION_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.BENEFITS_NOTIFICATION_CAPTION',
        },
        {
          controlName: 'benefitsDeadline',
          labelKey: 'ADMIN.DAYS_LEFT.BENEFITS_DEADLINE_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.BENEFITS_DEADLINE_CAPTION',
        },
      ],
      result: this.benefitsResult,
      preview: () => this.onPreviewBenefitsEmail(),
    },
    {
      titleKey: 'ADMIN.DAYS_LEFT.PAYHAWK_REMINDER_TITLE',
      descriptionKey: 'ADMIN.DAYS_LEFT.PAYHAWK_REMINDER_DESCRIPTION',
      recipientsKey: 'ADMIN.DAYS_LEFT.PAYHAWK_RECIPIENTS',
      dates: [
        {
          controlName: 'payhawk',
          labelKey: 'ADMIN.DAYS_LEFT.PAYHAWK_NOTIFICATION_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.PAYHAWK_NOTIFICATION_CAPTION',
        },
        {
          controlName: 'payhawkDeadline',
          labelKey: 'ADMIN.DAYS_LEFT.PAYHAWK_DEADLINE_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.PAYHAWK_DEADLINE_CAPTION',
        },
      ],
      result: this.payhawkResult,
      preview: () => this.onPreviewPayhawkEmail(),
    },
    {
      titleKey: 'ADMIN.DAYS_LEFT.TEAMS_RATES_REMINDER_TITLE',
      descriptionKey: 'ADMIN.DAYS_LEFT.TEAMS_RATES_REMINDER_DESCRIPTION',
      recipientsKey: 'ADMIN.DAYS_LEFT.TEAMS_RATES_RECIPIENTS',
      dates: [
        {
          controlName: 'teamsRates',
          labelKey: 'ADMIN.DAYS_LEFT.TEAMS_RATES_NOTIFICATION_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.TEAMS_RATES_NOTIFICATION_CAPTION',
        },
        {
          controlName: 'teamsRatesDeadline',
          labelKey: 'ADMIN.DAYS_LEFT.TEAMS_RATES_DEADLINE_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.TEAMS_RATES_DEADLINE_CAPTION',
        },
      ],
      result: this.teamsRatesResult,
      preview: () => this.onPreviewTeamsRatesEmail(),
    },
    {
      titleKey: 'ADMIN.DAYS_LEFT.MONTHLY_INVOICES_REMINDER_TITLE',
      descriptionKey: 'ADMIN.DAYS_LEFT.MONTHLY_INVOICES_REMINDER_DESCRIPTION',
      recipientsKey: 'ADMIN.DAYS_LEFT.MONTHLY_INVOICES_RECIPIENTS',
      dates: [
        {
          controlName: 'monthlyInvoices',
          labelKey: 'ADMIN.DAYS_LEFT.MONTHLY_INVOICES_NOTIFICATION_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.MONTHLY_INVOICES_NOTIFICATION_CAPTION',
        },
        {
          controlName: 'monthlyInvoicesDeadline',
          labelKey: 'ADMIN.DAYS_LEFT.MONTHLY_INVOICES_DEADLINE_LABEL',
          hintKey: 'ADMIN.DAYS_LEFT.MONTHLY_INVOICES_DEADLINE_CAPTION',
        },
      ],
      result: this.monthlyInvoicesResult,
      preview: () => this.onPreviewMonthlyInvoicesEmail(),
    },
  ];

  private savedValues: {
    notification: Date | null;
    deadline: Date | null;
    opsReminder: Date | null;
    workReportLeads: Date | null;
    workReportDeadline: Date | null;
    missingTimeLogFirst: Date | null;
    missingTimeLogSecond: Date | null;
    benefits: Date | null;
    benefitsDeadline: Date | null;
    payhawk: Date | null;
    payhawkDeadline: Date | null;
    teamsRates: Date | null;
    teamsRatesDeadline: Date | null;
    monthlyInvoices: Date | null;
    monthlyInvoicesDeadline: Date | null;
    customBody: string;
    customBodyFormat: ContentFormat;
    monthlyInvoicesCustomBody: string;
    monthlyInvoicesCustomBodyFormat: ContentFormat;
  } | null = null;

  private notificationValidator = (control: AbstractControl<Date | null>) => {
    const chosenDate = control.value;
    if (!chosenDate) return null;
    const deadlineDate = this.form?.controls.deadline.value;
    if (!deadlineDate) return null;
    return chosenDate > deadlineDate ? { afterDeadline: true } : null;
  };

  form = new FormGroup({
    notification: new FormControl<Date | null>(null, {
      validators: [this.notificationValidator],
    }),
    deadline: new FormControl<Date | null>(null, Validators.required),
    opsReminder: new FormControl<Date | null>(null),
    workReportLeads: new FormControl<Date | null>(null),
    workReportDeadline: new FormControl<Date | null>(null, Validators.required),
    missingTimeLogFirst: new FormControl<Date | null>(null),
    missingTimeLogSecond: new FormControl<Date | null>(null),
    benefits: new FormControl<Date | null>(null),
    benefitsDeadline: new FormControl<Date | null>(null, Validators.required),
    payhawk: new FormControl<Date | null>(null),
    payhawkDeadline: new FormControl<Date | null>(null, Validators.required),
    teamsRates: new FormControl<Date | null>(null),
    teamsRatesDeadline: new FormControl<Date | null>(null, Validators.required),
    monthlyInvoices: new FormControl<Date | null>(null),
    monthlyInvoicesDeadline: new FormControl<Date | null>(null, Validators.required),
    plannedLeavesCustomBody: new FormControl<string>('', { nonNullable: true }),
    plannedLeavesCustomBodyFormat: new FormControl<ContentFormat>('HTML', { nonNullable: true }),
    monthlyInvoicesCustomBody: new FormControl<string>('', { nonNullable: true }),
    monthlyInvoicesCustomBodyFormat: new FormControl<ContentFormat>('HTML', { nonNullable: true }),
  });

  constructor() {
    effect(() => {
      const month = this.month();
      const deadline = this.calculateDeadlineForMonth(month);
      this.form.patchValue({ deadline }, { emitEvent: false });
    });

    toObservable(this.month)
      .pipe(
        switchMap((month) =>
          this.notificationService.getNotificationSetup(month.format('YYYY-MM')),
        ),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((setup) => {
        if (!setup) {
          this.savedValues = null;
          this.form.reset(undefined, { emitEvent: false });
          this.hasChanges.set(false);
          this.employeeReminderResult.set(null);
          this.opsReminderResult.set(null);
          this.workReportLeadsResult.set(null);
          this.timeLogNotificationResult.set(null);
          this.benefitsResult.set(null);
          this.payhawkResult.set(null);
          this.teamsRatesResult.set(null);
          this.monthlyInvoicesResult.set(null);
          return;
        }
        this.employeeReminderResult.set(setup.employeeReminderResult ?? null);
        this.opsReminderResult.set(setup.opsReminderResult ?? null);
        this.workReportLeadsResult.set(setup.workReportLeadsNotificationResult ?? null);
        this.timeLogNotificationResult.set(setup.timeLogNotificationResult ?? null);
        this.benefitsResult.set(setup.benefitsNotificationResult ?? null);
        this.payhawkResult.set(setup.payhawkNotificationResult ?? null);
        this.teamsRatesResult.set(setup.teamsRatesNotificationResult ?? null);
        this.monthlyInvoicesResult.set(setup.monthlyInvoicesNotificationResult ?? null);
        this.deadlineDay.set(moment(setup.deadlineDate).date());
        this.savedValues = {
          deadline: setup.deadlineDate,
          notification: setup.plannedLeavesNotificationDate,
          opsReminder: setup.setupNotificationDate ?? null,
          workReportLeads: setup.workReportNotificationDate ?? null,
          workReportDeadline: setup.workReportDeadlineDate ?? null,
          missingTimeLogFirst: setup.missingTimeLogFirstNotificationDate ?? null,
          missingTimeLogSecond: setup.missingTimeLogSecondNotificationDate ?? null,
          benefits: setup.benefitsNotificationDate ?? null,
          benefitsDeadline: setup.benefitsDeadlineDate ?? null,
          payhawk: setup.payhawkNotificationDate ?? null,
          payhawkDeadline: setup.payhawkDeadlineDate ?? null,
          teamsRates: setup.teamsRatesNotificationDate ?? null,
          teamsRatesDeadline: setup.teamsRatesDeadlineDate ?? null,
          monthlyInvoices: setup.monthlyInvoicesNotificationDate ?? null,
          monthlyInvoicesDeadline: setup.monthlyInvoicesDeadlineDate ?? null,
          customBody: this.normalizeCustomBody(setup.plannedLeavesCustomBody),
          customBodyFormat: setup.plannedLeavesCustomBodyFormat ?? 'HTML',
          monthlyInvoicesCustomBody: this.normalizeCustomBody(setup.monthlyInvoicesCustomBody),
          monthlyInvoicesCustomBodyFormat: setup.monthlyInvoicesCustomBodyFormat ?? 'HTML',
        };
        this.form.patchValue(
          {
            deadline: this.savedValues.deadline,
            notification: this.savedValues.notification,
            opsReminder: this.savedValues.opsReminder,
            workReportLeads: this.savedValues.workReportLeads,
            workReportDeadline: this.savedValues.workReportDeadline,
            missingTimeLogFirst: this.savedValues.missingTimeLogFirst,
            missingTimeLogSecond: this.savedValues.missingTimeLogSecond,
            benefits: this.savedValues.benefits,
            benefitsDeadline: this.savedValues.benefitsDeadline,
            payhawk: this.savedValues.payhawk,
            payhawkDeadline: this.savedValues.payhawkDeadline,
            teamsRates: this.savedValues.teamsRates,
            teamsRatesDeadline: this.savedValues.teamsRatesDeadline,
            monthlyInvoices: this.savedValues.monthlyInvoices,
            monthlyInvoicesDeadline: this.savedValues.monthlyInvoicesDeadline,
            plannedLeavesCustomBody: this.savedValues.customBody,
            plannedLeavesCustomBodyFormat: this.savedValues.customBodyFormat,
            monthlyInvoicesCustomBody: this.savedValues.monthlyInvoicesCustomBody,
            monthlyInvoicesCustomBodyFormat: this.savedValues.monthlyInvoicesCustomBodyFormat,
          },
          { emitEvent: false },
        );
        this.hasChanges.set(false);
      });
  }

  ngOnInit() {
    this.form.valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      if (!this.savedValues) return;
      const {
        notification,
        deadline,
        opsReminder,
        workReportLeads,
        workReportDeadline,
        missingTimeLogFirst,
        missingTimeLogSecond,
        benefits,
        benefitsDeadline,
        payhawk,
        payhawkDeadline,
        teamsRates,
        teamsRatesDeadline,
        monthlyInvoices,
        monthlyInvoicesDeadline,
        plannedLeavesCustomBody,
        plannedLeavesCustomBodyFormat,
        monthlyInvoicesCustomBody,
        monthlyInvoicesCustomBodyFormat,
      } = this.form.value;
      this.hasChanges.set(
        !this.datesEqual(notification, this.savedValues.notification) ||
          !this.datesEqual(deadline, this.savedValues.deadline) ||
          !this.datesEqual(opsReminder, this.savedValues.opsReminder) ||
          !this.datesEqual(workReportLeads, this.savedValues.workReportLeads) ||
          !this.datesEqual(workReportDeadline, this.savedValues.workReportDeadline) ||
          !this.datesEqual(missingTimeLogFirst, this.savedValues.missingTimeLogFirst) ||
          !this.datesEqual(missingTimeLogSecond, this.savedValues.missingTimeLogSecond) ||
          !this.datesEqual(benefits, this.savedValues.benefits) ||
          !this.datesEqual(benefitsDeadline, this.savedValues.benefitsDeadline) ||
          !this.datesEqual(payhawk, this.savedValues.payhawk) ||
          !this.datesEqual(payhawkDeadline, this.savedValues.payhawkDeadline) ||
          !this.datesEqual(teamsRates, this.savedValues.teamsRates) ||
          !this.datesEqual(teamsRatesDeadline, this.savedValues.teamsRatesDeadline) ||
          !this.datesEqual(monthlyInvoices, this.savedValues.monthlyInvoices) ||
          !this.datesEqual(monthlyInvoicesDeadline, this.savedValues.monthlyInvoicesDeadline) ||
          this.normalizeCustomBody(plannedLeavesCustomBody) !== this.savedValues.customBody ||
          (plannedLeavesCustomBodyFormat ?? 'HTML') !== this.savedValues.customBodyFormat ||
          this.normalizeCustomBody(monthlyInvoicesCustomBody) !==
            this.savedValues.monthlyInvoicesCustomBody ||
          (monthlyInvoicesCustomBodyFormat ?? 'HTML') !==
            this.savedValues.monthlyInvoicesCustomBodyFormat,
      );
    });

    this.form.controls.deadline.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        if (!value) return;
        this.deadlineDay.set(value.getDate());
        this.form.controls.notification.updateValueAndValidity({ emitEvent: false });
      });

    this.bodyFormatChange$
      .pipe(
        switchMap((target) => {
          const content = this.form.controls.plannedLeavesCustomBody.value;
          this.isConvertingBody.set(true);
          this.form.controls.plannedLeavesCustomBody.disable({ emitEvent: false });
          return this.notificationService.convertBody(content, target).pipe(
            map((converted) => ({ target, converted })),
            catchError(() => {
              this.resetFormatToggle(this.form.controls.plannedLeavesCustomBodyFormat.value);
              return EMPTY;
            }),
            finalize(() => {
              this.form.controls.plannedLeavesCustomBody.enable({ emitEvent: false });
              this.isConvertingBody.set(false);
            }),
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(({ target, converted }) => {
        this.form.patchValue({
          plannedLeavesCustomBody: converted,
          plannedLeavesCustomBodyFormat: target,
        });
      });
  }

  onBodyFormatChange(targetFormat: ContentFormat): void {
    const currentFormat = this.form.controls.plannedLeavesCustomBodyFormat.value;
    if (targetFormat === currentFormat) return;
    if (!this.normalizeCustomBody(this.form.controls.plannedLeavesCustomBody.value)) {
      this.form.controls.plannedLeavesCustomBodyFormat.setValue(targetFormat);
      return;
    }
    this.resetFormatToggle(currentFormat);
    this.dialog
      .open(DialogComponent, {
        data: {
          title: this.translate.instant('ADMIN.DAYS_LEFT.CONVERT_FORMAT_TITLE'),
          description: this.translate.instant('ADMIN.DAYS_LEFT.CONVERT_FORMAT_DESCRIPTION'),
          sharedButtonClass: DIALOG_BUTTONS.warningButton,
          sharedButtonText: this.translate.instant('ADMIN.DAYS_LEFT.CONVERT_FORMAT_CONFIRM'),
        },
      })
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((confirmed) => {
        if (confirmed) {
          this.bodyFormatChange$.next(targetFormat);
        }
      });
  }

  private resetFormatToggle(format: ContentFormat): void {
    this.form.controls.plannedLeavesCustomBodyFormat.setValue(format, { emitEvent: false });
    const group = this.formatToggleGroup();
    if (group) {
      group.value = format;
    }
  }

  private normalizeCustomBody(value: string | null | undefined): string {
    let normalized = (value ?? '').trim();
    const open = '<div class="tinymce-body">';
    const close = '</div>';
    while (normalized.startsWith(open) && normalized.endsWith(close)) {
      normalized = normalized.slice(open.length, normalized.length - close.length).trim();
    }
    return normalized;
  }

  onPreviewEmail(): void {
    this.openPreview(
      this.notificationService.getPlannedLeavesEmailPreview(
        this.month().format('YYYY-MM'),
        this.normalizeCustomBody(this.form.controls.plannedLeavesCustomBody.value),
        this.form.controls.plannedLeavesCustomBodyFormat.value,
      ),
    );
  }

  onPreviewOpsEmail(): void {
    this.openPreview(this.notificationService.getOpsReminderEmailPreview(this.month().format('YYYY-MM')));
  }

  onPreviewWorkReportLeadsEmail(): void {
    this.openPreview(this.notificationService.getWorkReportLeadsEmailPreview(this.month().format('YYYY-MM')));
  }

  onPreviewMissingTimeLogEmail(): void {
    this.openPreview(this.notificationService.getMissingTimeLogEmailPreview(this.month().format('YYYY-MM')));
  }

  onPreviewBenefitsEmail(): void {
    this.openPreview(this.notificationService.getBenefitsReminderEmailPreview(this.month().format('YYYY-MM')));
  }

  onPreviewPayhawkEmail(): void {
    this.openPreview(this.notificationService.getPayhawkReminderEmailPreview(this.month().format('YYYY-MM')));
  }

  onPreviewTeamsRatesEmail(): void {
    this.openPreview(this.notificationService.getTeamsRatesReminderEmailPreview(this.month().format('YYYY-MM')));
  }

  onPreviewMonthlyInvoicesEmail(): void {
    this.openPreview(
      this.notificationService.getMonthlyInvoicesReminderEmailPreview(
        this.month().format('YYYY-MM'),
        this.normalizeCustomBody(this.form.controls.monthlyInvoicesCustomBody.value),
        this.form.controls.monthlyInvoicesCustomBodyFormat.value,
      ),
    );
  }

  private openPreview(fetch$: Observable<EmailPreview>): void {
    this.loadingPreview.set(true);
    fetch$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (preview) => {
        this.loadingPreview.set(false);
        this.dialog.open(EmailPreviewDialogComponent, { data: preview });
      },
      error: () => {
        this.loadingPreview.set(false);
        this.alertService.showError('ADMIN.DAYS_LEFT.PREVIEW_EMAIL_ERROR');
      },
    });
  }

  onSaveChanges(): void {
    this.saveSetup();
  }

  private saveSetup(): void {
    if (this.form.controls.notification.hasError('afterDeadline')) return;
    const deadline = this.form.controls.deadline.value;
    const notification = this.form.controls.notification.value;
    const opsReminder = this.form.controls.opsReminder.value;
    const workReportLeads = this.form.controls.workReportLeads.value;
    const workReportDeadline = this.form.controls.workReportDeadline.value;
    const missingTimeLogFirst = this.form.controls.missingTimeLogFirst.value;
    const missingTimeLogSecond = this.form.controls.missingTimeLogSecond.value;
    const benefits = this.form.controls.benefits.value;
    const benefitsDeadline = this.form.controls.benefitsDeadline.value;
    const payhawk = this.form.controls.payhawk.value;
    const payhawkDeadline = this.form.controls.payhawkDeadline.value;
    const teamsRates = this.form.controls.teamsRates.value;
    const teamsRatesDeadline = this.form.controls.teamsRatesDeadline.value;
    const monthlyInvoices = this.form.controls.monthlyInvoices.value;
    const monthlyInvoicesDeadline = this.form.controls.monthlyInvoicesDeadline.value;
    const customBody = this.normalizeCustomBody(this.form.controls.plannedLeavesCustomBody.value);
    const customBodyFormat = this.form.controls.plannedLeavesCustomBodyFormat.value;
    const monthlyInvoicesCustomBody = this.normalizeCustomBody(
      this.form.controls.monthlyInvoicesCustomBody.value,
    );
    const monthlyInvoicesCustomBodyFormat =
      this.form.controls.monthlyInvoicesCustomBodyFormat.value;
    if (!deadline || !notification || !opsReminder) return;

    const setup = {
      deadlineDate: deadline,
      plannedLeavesNotificationDate: notification,
      setupNotificationDate: opsReminder,
      workReportNotificationDate: workReportLeads,
      workReportDeadlineDate: workReportDeadline,
      missingTimeLogFirstNotificationDate: missingTimeLogFirst,
      missingTimeLogSecondNotificationDate: missingTimeLogSecond,
      benefitsNotificationDate: benefits,
      benefitsDeadlineDate: benefitsDeadline,
      payhawkNotificationDate: payhawk,
      payhawkDeadlineDate: payhawkDeadline,
      teamsRatesNotificationDate: teamsRates,
      teamsRatesDeadlineDate: teamsRatesDeadline,
      monthlyInvoicesNotificationDate: monthlyInvoices,
      monthlyInvoicesDeadlineDate: monthlyInvoicesDeadline,
      plannedLeavesCustomBody: customBody,
      plannedLeavesCustomBodyFormat: customBodyFormat,
      monthlyInvoicesCustomBody: monthlyInvoicesCustomBody,
      monthlyInvoicesCustomBodyFormat: monthlyInvoicesCustomBodyFormat,
    };

    this.notificationService
      .saveNotificationSetup(setup, this.month(), false)
      .pipe(
        catchError((err: HttpErrorResponse) => {
          if (err.status !== 422) {
            return throwError(() => err);
          }
          return this.dialog
            .open(DialogComponent, {
              data: {
                title: this.translate.instant('ADMIN.DAYS_LEFT.NON_WORKING_DATE_WARNING_TITLE'),
                description: err.error,
                sharedButtonClass: DIALOG_BUTTONS.warningButton,
                sharedButtonText: this.translate.instant(
                  'ADMIN.DAYS_LEFT.NON_WORKING_DATE_WARNING_CONFIRM',
                ),
              },
            })
            .afterClosed()
            .pipe(
              switchMap((confirmed) =>
                confirmed
                  ? this.notificationService.saveNotificationSetup(setup, this.month(), true)
                  : EMPTY,
              ),
            );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        this.savedValues = {
          notification,
          deadline,
          opsReminder,
          workReportLeads,
          workReportDeadline,
          missingTimeLogFirst,
          missingTimeLogSecond,
          benefits,
          benefitsDeadline,
          payhawk,
          payhawkDeadline,
          teamsRates,
          teamsRatesDeadline,
          monthlyInvoices,
          monthlyInvoicesDeadline,
          customBody: customBody ?? '',
          customBodyFormat: customBodyFormat ?? 'HTML',
          monthlyInvoicesCustomBody: monthlyInvoicesCustomBody ?? '',
          monthlyInvoicesCustomBodyFormat: monthlyInvoicesCustomBodyFormat ?? 'HTML',
        };
        this.hasChanges.set(false);
        this.alertService.showSuccess('ADMIN.DAYS_LEFT.SAVE_CHANGES_SUCCESS');
        this.saved.emit();
      });
  }

  private calculateDeadlineForMonth(month: moment.Moment): Date {
    const deadline = month
      .clone()
      .date(untracked(() => this.deadlineDay()))
      .toDate();
    return this.shiftToFridayIfWeekend(deadline);
  }

  private datesEqual(a: Date | null | undefined, b: Date | null | undefined): boolean {
    if (!a && !b) return true;
    if (!a || !b) return false;
    return (
      a.getFullYear() === b.getFullYear() &&
      a.getMonth() === b.getMonth() &&
      a.getDate() === b.getDate()
    );
  }

  private shiftToFridayIfWeekend(inputDate: Date): Date {
    const date = new Date(inputDate);
    const day = date.getDay();

    if (day === 6) date.setDate(date.getDate() - 1);
    if (day === 0) date.setDate(date.getDate() - 2);

    return date;
  }
}
